/**
 * Google Analytics Utility
 * Tracks user interactions and page views throughout the application
 */

import ReactGA from 'react-ga4';

// Initialize Google Analytics
let isInitialized = false;

export const initGA = () => {
  try {
    const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
    
    if (measurementId && !isInitialized) {
      ReactGA.initialize(measurementId, {
        gaOptions: {
          anonymizeIp: true, // Anonymize IP for privacy
        },
        gtagOptions: {
          send_page_view: false, // We'll manually send page views
        }
      });
      isInitialized = true;
      console.log('Google Analytics initialized:', measurementId);
    } else if (!measurementId) {
      console.log('Google Analytics not initialized - VITE_GOOGLE_ANALYTICS_ID not found');
    }
  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
  }
};

// Track page view
export const trackPageView = (path, title) => {
  if (isInitialized) {
    ReactGA.send({ 
      hitType: 'pageview', 
      page: path,
      title: title || document.title
    });
  }
};

// Track custom events
export const trackEvent = (category, action, label, value) => {
  if (isInitialized) {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  }
};

// Pre-defined tracking functions for common events

// User authentication events
export const trackSignUp = (method = 'email') => {
  trackEvent('User', 'Sign Up', method);
};

export const trackSignIn = (method = 'email') => {
  trackEvent('User', 'Sign In', method);
};

export const trackSignOut = () => {
  trackEvent('User', 'Sign Out');
};

// Vendor events
export const trackVendorView = (vendorId, vendorName, category) => {
  trackEvent('Vendor', 'View Profile', `${category} - ${vendorName}`, vendorId);
};

export const trackVendorSearch = (query, category, location) => {
  trackEvent('Search', 'Vendor Search', `${query} | ${category} | ${location}`);
};

export const trackVendorInquiry = (vendorId, vendorCategory) => {
  trackEvent('Engagement', 'Send Inquiry', vendorCategory, vendorId);
};

export const trackVendorSave = (vendorId, vendorCategory) => {
  trackEvent('Engagement', 'Save Vendor', vendorCategory, vendorId);
};

// Booking events
export const trackBookingCreated = (vendorCategory, amount) => {
  trackEvent('Booking', 'Create Booking', vendorCategory, amount);
};

export const trackBookingConfirmed = (bookingId, amount) => {
  trackEvent('Booking', 'Confirm Booking', bookingId, amount);
};

export const trackBookingCancelled = (bookingId, reason) => {
  trackEvent('Booking', 'Cancel Booking', reason, bookingId);
};

// Planning tools events
export const trackChecklistCreated = (templateName) => {
  trackEvent('Planning Tools', 'Create Checklist', templateName);
};

export const trackChecklistTaskCompleted = (taskTitle) => {
  trackEvent('Planning Tools', 'Complete Task', taskTitle);
};

export const trackBudgetCreated = (totalAmount) => {
  trackEvent('Planning Tools', 'Create Budget', 'Total', totalAmount);
};

export const trackExpenseAdded = (category, amount) => {
  trackEvent('Planning Tools', 'Add Expense', category, amount);
};

// Review events
export const trackReviewSubmitted = (vendorCategory, rating) => {
  trackEvent('Engagement', 'Submit Review', vendorCategory, rating);
};

// Invoice events
export const trackInvoiceCreated = (amount) => {
  trackEvent('Invoice', 'Create Invoice', 'Amount', amount);
};

export const trackInvoiceSent = (invoiceId) => {
  trackEvent('Invoice', 'Send Invoice', invoiceId);
};

export const trackInvoicePaid = (invoiceId, amount) => {
  trackEvent('Invoice', 'Mark Paid', invoiceId, amount);
};

// Subscription events (for vendors)
export const trackSubscriptionUpgrade = (fromTier, toTier, amount) => {
  trackEvent('Subscription', 'Upgrade', `${fromTier} to ${toTier}`, amount);
};

export const trackSubscriptionDowngrade = (fromTier, toTier) => {
  trackEvent('Subscription', 'Downgrade', `${fromTier} to ${toTier}`);
};

// Payment events
export const trackPaymentInitiated = (amount, method) => {
  trackEvent('Payment', 'Initiate', method, amount);
};

export const trackPaymentSuccess = (amount, method) => {
  trackEvent('Payment', 'Success', method, amount);
};

export const trackPaymentFailed = (amount, error) => {
  trackEvent('Payment', 'Failed', error, amount);
};

// Error tracking
export const trackError = (errorType, errorMessage, location) => {
  trackEvent('Error', errorType, `${location}: ${errorMessage}`);
};

// Timing events
export const trackTiming = (category, variable, time, label) => {
  if (isInitialized) {
    ReactGA.gtag('event', 'timing_complete', {
      name: variable,
      value: time,
      event_category: category,
      event_label: label,
    });
  }
};

// E-commerce tracking (for vendor subscriptions)
export const trackPurchase = (transactionId, value, items) => {
  if (isInitialized) {
    ReactGA.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: 'USD',
      items: items,
    });
  }
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackSignUp,
  trackSignIn,
  trackSignOut,
  trackVendorView,
  trackVendorSearch,
  trackVendorInquiry,
  trackVendorSave,
  trackBookingCreated,
  trackBookingConfirmed,
  trackBookingCancelled,
  trackChecklistCreated,
  trackChecklistTaskCompleted,
  trackBudgetCreated,
  trackExpenseAdded,
  trackReviewSubmitted,
  trackInvoiceCreated,
  trackInvoiceSent,
  trackInvoicePaid,
  trackSubscriptionUpgrade,
  trackSubscriptionDowngrade,
  trackPaymentInitiated,
  trackPaymentSuccess,
  trackPaymentFailed,
  trackError,
  trackTiming,
  trackPurchase,
};
